import useSubScriptionMutation from "@/api/mutation/useSubScriptionMutation";
import { Button } from "../ui/button";
import CharmTick from "@/assets/svg/CharmTick.svg";
import crossNew from "@/assets/svg/CrossIcon.svg";
import {
  BillingCycleSchema,
  ExtendPlanTypeEnumSchema,
  PlanTypeEnumSchema,
} from "@backend/src/schemas/enums";
import { useUser } from "@/hooks/useUser";
import { toast } from "react-toastify";
import { errorConfig } from "@/lib/toastError";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useGetInvoicePortalQuery from "@/api/query/useInvoiceProtalQuary";
import useUpgradeSubScriptionMutation from "@/api/mutation/useUpgradeSubScriptionMutation";
import Dialog from "../common/Dialog";
import Warning from "@/assets/svg/Warning.svg";
import Loader from "../common/Loader";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import GreenChackMark from "@/assets/svg/GreenChackMark.svg";
import RedCharmTick from "@/assets/svg/RedCharmTick.svg";
import Spinner from "../ui/spinner";
import dateFormater from "@/helperFuntions/dateFormater";
import useRibbonsListQuery from "@/api/query/useRibbonMessageQuery";
import useProjectQuery from "@/api/query/useProjectQuery";
import { FourteenDayCounterStartNow } from "../common/DaysCounter";

export interface DowngradeDetails {
  currentPlan: CurrentPlan;
  downgradePlan: DowngradePlan;
}

export interface CurrentPlan {
  planType: string;
  billingCycle: string;
}

export interface DowngradePlan {
  planType: string;
  billingCycle: string;
}

const PaymentPlans = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { mutate: checkout, isPending } = useSubScriptionMutation();
  const { mutate: upgradeToEnterprise } = useUpgradeSubScriptionMutation();
  const { user } = useUser();
  const [isAnnual, setIsAnnual] = useState(false);
  const navigate = useNavigate();
  const [isPaymentSuccess, setIsPaymentSuccess] = useState<{
    loading: boolean;
    status: "PENDING" | "SUCCESS" | "ERROR";
  }>({
    loading: false,
    status: "PENDING",
  });
  const userQuary = useCurrentUserQuery();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loader, setLoader] = useState(false);
  const ribbonsListQuery = useRibbonsListQuery();
  const [discount, setDiscount] = useState<{
    discount: number;
    months: number;
    couponCode: string;
  }>();
  const [coupon] = useSearchParams();
  const [selectedPlan, setSelectedPlan] =
    useState<keyof typeof PlanTypeEnumSchema>();
  const [downgrade, setDowngrade] = useState<keyof typeof PlanTypeEnumSchema>();

  const handleCheckout = (
    planType: keyof typeof PlanTypeEnumSchema,
    isUpgrade: boolean = false
  ) => {
    if (!user?.email) {
      toast.error("Please login to continue");
      return;
    }
    if (text(planType) == t("DowngradeToThisPlan") && !downgrade) {
      setDowngrade(planType);
      return;
    }
    if (
      user.subscription &&
      user.subscription.planType &&
      text(planType) == t("UpgradeToThisPlan") &&
      (user.subscription.customerId !== ExtendPlanTypeEnumSchema.FREE_TRIAL ) 
    ) {
      if (isUpgrade) {
        setIsPaymentSuccess({ loading: true, status: "PENDING" });
      }
      upgradeToEnterprise(
        {
          email: user?.email,
          planType: errorMessage && selectedPlan ? selectedPlan : planType,
          billingCycle: isAnnual
            ? BillingCycleSchema.YEAR
            : BillingCycleSchema.MONTH,
          isUpgrade: isUpgrade,
        },
        {
          onSuccess(data) {
            setLoader(false);
            setErrorMessage("");
            toast.success(data.data.message, errorConfig);
            userQuary.refetch();
            setIsPaymentSuccess({ loading: true, status: "SUCCESS" });
          },
          onError(error) {
            if (isUpgrade) {
              setIsPaymentSuccess({ loading: true, status: "ERROR" });
            }
            setLoader(false);
            if (
              error.response?.data.message.includes(
                "You card will be charged only"
              )
            ) {
              setErrorMessage(error.response.data.message);
              setSelectedPlan(planType);
            } else {
              toast.error(error.response?.data.message, errorConfig);
            }
          },
        }
      );
    } else {
      setLoader(true);
      setDowngrade(undefined);
      const couponCode = coupon.get("coupon") ?? "";
      checkout(
        {
          email: user?.email,
          planType: planType,
          billingCycle: isAnnual
            ? BillingCycleSchema.YEAR
            : BillingCycleSchema.MONTH,
          couponCode: couponCode,
        },
        {
          onSuccess(data) {
            navigate(`/checkout/${data.data.data.client_secret}`);
            onClose();
            setLoader(false);
          },
          onError(error) {
            if (
              error.response?.data.message.includes(
                "Your plan will be downgraded"
              )
            ) {
              toast.error(
                t(
                  "Your plan will be downgraded, but is still available until the end of your billing period on"
                ) +
                  ` (${
                    error.response?.data.message.split("billing period on")[1]
                  })`,
                errorConfig
              );
            } else {
              toast.error(error.response?.data.message, errorConfig);
            }
            setLoader(false);
            userQuary.refetch();
          },
        }
      );
    }
  };

  const { refetch, isRefetching } = useGetInvoicePortalQuery();
  const invoicePortal = () => {
    refetch()
      .then((res) => {
        if (res.data) {
          window.open(res.data.data.data, "_blank");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data.message, errorConfig);
      });
  };
  const allow = (key: keyof typeof PlanTypeEnumSchema) => {
    if(user?.subscription?.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) return false;
    if (user) {
      return (
        user.subscription?.planType === key &&
        user.subscription?.customerId &&
        user.subscription?.subScriptionId &&
        user.subscription?.billingCycle ==
          (isAnnual ? BillingCycleSchema.YEAR : BillingCycleSchema.MONTH)
      );
    }
    return false;
  };

  const text = (key: keyof typeof PlanTypeEnumSchema) => {
    if (user && user.subscription) {
      if(user.subscription.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) {
        return t("UpgradeToThisPlan");
      }
      if (
        user.subscription?.billingCycle === BillingCycleSchema.MONTH &&
        isAnnual
      ) {
        return t("UpgradeToThisPlan");
      } else if (
        user?.subscription?.billingCycle === BillingCycleSchema.YEAR &&
        !isAnnual
      ) {
        return t("DowngradeToThisPlan");
      } else {
        if (
          key == PlanTypeEnumSchema.BUSINESS &&
          user.subscription?.planType == PlanTypeEnumSchema.ENTERPRISE
        ) {
          return t("DowngradeToThisPlan");
        }
        if (
          key == PlanTypeEnumSchema.ENTERPRISE &&
          user.subscription?.planType == PlanTypeEnumSchema.BUSINESS
        ) {
          return t("UpgradeToThisPlan");
        }
      }
    }
    return t("UpgradeToThisPlan");
  };
  const location = window.location.pathname;
  const projectQuery = useProjectQuery();

  useEffect(() => {
    if (ribbonsListQuery.data?.data.data[0]) {
      //   const ribbon = ribbonsListQuery.data?.data.data[0];
      //   const currantTime = new Date();
      //   const ribbonStartDate = new Date(ribbon.startDate);
      //   const ribbonExpiry = new Date(ribbon.validUntil);
      //   currantTime.setHours(0, 0, 0, 0);
      //   ribbonStartDate.setHours(0, 0, 0, 0);
      //   ribbonExpiry.setHours(0, 0, 0, 0);

      //   const isTodayStart = currantTime.getTime() === ribbonStartDate.getTime();
      //   const isTodayExpiry = currantTime.getTime() === ribbonExpiry.getTime();
      //   const isWithinRange = currantTime >= ribbonStartDate && currantTime <= ribbonExpiry;
      //   if (isTodayStart || isTodayExpiry || isWithinRange) {
      //     setDiscount({
      //       discount: ribbon.discountPercentage,
      //       months: ribbon.couponDuration,
      //       couponCode: ribbon.couponCode,
      //     });
      //   }
      //   if (user && user.subscription?.planType == PlanTypeEnumSchema.ENTERPRISE) {
      //     setDiscount(undefined)
      //   }
      if (coupon.get("coupon")) {
        const ribbon = ribbonsListQuery.data?.data.data[0];

        setDiscount({
          discount: ribbon.discountPercentage,
          months: ribbon.couponDuration,
          couponCode: ribbon.couponCode,
        });
      }
    }
  }, [ribbonsListQuery.data?.data.data, coupon]);

  useEffect(() => {
    if (user?.subscription?.billingCycle) {
      setIsAnnual(user?.subscription?.billingCycle == BillingCycleSchema.YEAR);
    }
  }, [user]);

  const cancel = useCallback(
    (key: keyof typeof PlanTypeEnumSchema) => {
      if (user?.subscription?.downgradeDetails && user?.subscription?.endDate) {
        const downgradeDetails = JSON.parse(
          user.subscription.downgradeDetails
        ) as DowngradeDetails;
        return (
          downgradeDetails.downgradePlan.billingCycle ===
            (!isAnnual ? BillingCycleSchema.MONTH : BillingCycleSchema.YEAR) &&
          downgradeDetails.downgradePlan.planType === key
        );
      }

      if (
        key === PlanTypeEnumSchema.BUSINESS &&
        !isAnnual &&
        user?.subscription?.endDate
      ) {
        return true;
      }

      return false;
    },
    [isAnnual, user]
  );

  function getDiscountPercentage(
    discountPercentage: number,
    actualValue: number
  ): string {
    const discountValue = (discountPercentage / 100) * actualValue;
    const finalPrice = actualValue - discountValue;
    return t("subscription.offer", {
      count: isAnnual ? 1 : discount?.months,
      period: isAnnual ? t("subscription.year") : t("subscription.months"),
      price: finalPrice.toFixed(2),
    });
  }

  return (
    <div className="overflow-auto  h-full py-10 p-4 ">
      <div className="flex flex-col flex-1 items-center justify-center x-auto">
        {loader && (
          <Loader className="absolute h-full w-full top-0 left-0  z-[99]" />
        )}
        <div className="flex justify-between items-center mb-8 w-full ">
          <div className="flex justify-between items-end gap-4">
            <h1 className="text-3xl text-gray-450 font-bold">
              {t("UpgradePlan")}
            </h1>
            <div className="flex text-gray-450  justify-between items-center gap-2">
              <div>{t("Monthly")}</div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isAnnual}
                  onChange={() => setIsAnnual(!isAnnual)}
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-600 dark:peer-focus:ring-primary-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-400"></div>
              </label>
              <div>
                {t("Annual")}{" "}
                <span className="text-xs font-light text-gray-450">
                  ({t("2MonthsFree")} / {t("20%off")})
                </span>
              </div>
            </div>
          </div>
          {location !== "/plans" && (
            <img
              src={crossNew}
              alt="cross"
              className="cursor-pointer w-5 min-w-5"
              onClick={() => {
                onClose();
              }}
            />
          )}
        </div>
        <div className="flex flex-col md:flex-row gap-8 w-full justify-center">
          {user?.subscription?.customerId === "FREE_TRIAL" ? (
            <div className="bg-white relative flex flex-col justify-between rounded-2xl p-9 w-72 text-nowrap max-w-80 text-black border-2 border-primary-600 shadow-xl shadow-gray-500">
              <div className="text-center mb-5">
                <div className="flex flex-col items-center justify-center gap-1">
                  <h2 className="text-3xl font-semibold">{t("Free_Trial")}</h2>
                  <FourteenDayCounterStartNow startDate={user.subscription.createdAt} />
                </div>
                <hr className="border-gray-400 rounded-3xl mt-5" />
                <ul className="mt-6 text-sm">
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("UnlimitedProjects")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="min-w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("UnlimitedProjectMembers")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("Resources workload")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("RiskManagement")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("ProjectDashboard")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="min-w-4 h-4 invert" />
                    </span>{" "}
                    {t("PortfolioDashboard")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("App Client")}
                  </li>
                  <li className="flex items-center mb-2">
                    <span className="text-green-500 mr-2">
                      <img src={CharmTick} className="w-4 min-h-4 invert" />
                    </span>{" "}
                    {t("OnboardingAndTraining")}
                  </li>
                </ul>
                {user.subscription.customerId ===
                  ExtendPlanTypeEnumSchema.FREE_TRIAL && (
                  <div className="absolute -bottom-6 right-1/2 translate-x-1/2 bg-primary-600 text-white px-1  rounded-b-xl  text-center">
                    <span className="text-xs font-light text-white">
                      {t("CurrentPlan")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              className={`bg-white relative flex flex-col justify-start rounded-2xl p-10 w-72 text-nowrap max-w-80 text-black border border-gray-500  ${
                !user?.subscription?.planType || allow(PlanTypeEnumSchema.FREE)
                  ? "border-2 border-primary-600  shadow-xl  shadow-gray-500"
                  : ""
              }`}
            >
              <div className="text-center mb-5">
                <h2 className="text-3xl font-semibold mb-3">{t("Free")}</h2>
                <p className="text-md font-light mb-3 text-[#F2F2F2]"></p>
                {/* <p className="text-3xl font-bold mb-3">
                  Free
                  <span className="text-xs font-light text-[#F2F2F2]">
                    
                  </span>
                </p> */}
                <hr className="border-gray-400 rounded-3xl" />
              </div>
              <ul className="mb-6 text-sm">
                <li className="flex items-center mb-2">
                  <span className="mr-2">
                    <img src={CharmTick} className="w-4 min-h-4" />
                  </span>{" "}
                  2 {t("projects")}
                </li>
                <li className="flex items-center mb-2">
                  <span className="mr-2">
                    <img src={CharmTick} className="w-4 min-h-4" />
                  </span>{" "}
                  1 {t("ProjectManager")}
                </li>
                <li className="flex items-center mb-2">
                  <span className="mr-2">
                    <img src={CharmTick} className="w-4 min-h-4" />
                  </span>{" "}
                  2 {t("ProjectMembers")}
                </li>
                {/* <li className="flex items-center mb-2">
                  <span className="mr-2">
                    <img src={CharmTick} className="w-4 min-h-4" />
                  </span>{" "}
                  Risk Management
                </li>
                <li className="flex items-center mb-2">
                  <span className="mr-2">
                    <img src={CharmTick} className="w-4 min-h-4" />
                  </span>{" "}
                  Project Dashboard
                </li>
                <li className="flex items-center mb-2">
                  <span className="mr-2">
                    <img src={CharmTick} className="w-4 min-h-4" />
                  </span>{" "}
                  Portfolio Dashboard
                </li> */}
              </ul>
              {/* <Button
                variant={"default"}
                className="w-full bg-white hover:bg-white/90 text-black font-semibold"
                onClick={() => handleCheckout(PlanTypeEnumSchema.BUSINESS)}
              >
                Upgrade to this Plan
              </Button> */}
              {(!user?.subscription ||
                user?.subscription?.planType == PlanTypeEnumSchema.FREE) && (
                <div className="absolute -bottom-6 right-1/2 translate-x-1/2 bg-primary-600 text-white px-1  rounded-b-xl  text-center">
                  <span className="text-xs font-light text-white">
                    {t("CurrentPlan")}
                  </span>
                </div>
              )}
            </div>
          )}
          <div
            className={`relative bg-primary-500 flex flex-col justify-between rounded-2xl p-10 w-72 max-w-80 text-white  ${
              allow(PlanTypeEnumSchema.BUSINESS)
                ? "border-2 border-primary-600  shadow-xl  shadow-gray-500"
                : ""
            }`}
          >
            <div className="text-center mb-5">
              <h2 className="text-3xl font-semibold mb-3">{t("Business")}</h2>
              <p className="text-md font-light mb-3 text-[#F2F2F2]">
                {t("IdealForFreelancersAndProjectManagers")}
              </p>
              <p className={`text-3xl font-bold mb-3`}>
                <span className={`${discount ? "line-through" : ""}`}>
                  {isAnnual ? "€390" : "€39"}&nbsp;
                </span>
                <span
                  className={` text-xs font-light text-[#F2F2F2] ${
                    discount ? "line-through" : ""
                  }`}
                >
                  {isAnnual ? t("perYear") : t("perMonth")}
                </span>
                <div className="text-sm font-light">
                  {discount
                    ? isAnnual
                      ? getDiscountPercentage(discount.discount, 390)
                      : getDiscountPercentage(discount.discount, 39)
                    : ""}
                </div>
              </p>
              <hr className="border-white rounded-3xl" />
            </div>
            <ul className="mb-6 text-sm">
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                5 {t("projects")}
              </li>
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                1 {t("ProjectManager")}
              </li>
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                5 {t("ProjectMembers")}
              </li>
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("Resources workload")}
              </li>
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("RiskManagement")}
              </li>
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("ProjectDashboard")}
              </li>
              <li className="flex items-center mb-2">
                <span className="mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("PortfolioDashboard")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("OnboardingAndTraining")}
              </li>
            </ul>
            <Button
              variant={"default"}
              disabled={
                (isPending && isRefetching) ||
                cancel(PlanTypeEnumSchema.BUSINESS)
              }
              isLoading={isPending || isRefetching}
              className="w-full bg-white hover:bg-white/90 text-black font-semibold"
              onClick={() => {
                allow(PlanTypeEnumSchema.BUSINESS)
                  ? invoicePortal()
                  : handleCheckout(PlanTypeEnumSchema.BUSINESS);
              }}
            >
              {allow(PlanTypeEnumSchema.BUSINESS)
                ? t("ViewInvoice")
                : text(PlanTypeEnumSchema.BUSINESS)}
            </Button>
            {cancel(PlanTypeEnumSchema.BUSINESS) &&
              user?.subscription?.endDate && (
                <span className="text-[0.65rem] text-center text-white/50">
                  {t(
                    "Your plan will be downgraded, but is still available until the end of your billing period on"
                  ) + ` (${dateFormater(user?.subscription?.endDate)})`}
                </span>
              )}
            {allow(PlanTypeEnumSchema.BUSINESS) && (
              <div className="absolute -bottom-6 right-1/2 translate-x-1/2 bg-primary-600 text-white px-1  rounded-b-xl  text-center">
                <span className="text-xs font-light text-white">
                  {t("CurrentPlan")}
                </span>
              </div>
            )}
          </div>
          <div
            className={`relative bg-[#0D062D] rounded-2xl p-10 flex flex-col justify-between w-72 max-w-80 text-white  ${
              allow(PlanTypeEnumSchema.ENTERPRISE)
                ? "border-2 border-primary-600  shadow-xl shadow-gray-500"
                : ""
            }`}
          >
            <div className="text-center mb-2">
              <h2 className="text-3xl font-semibold mb-3">{t("Enterprise")}</h2>
              <p className="text-md font-light mb-3 text-[#BCBEC1]">
                {t("IdealForMidSizeAndLargeTeams")}
              </p>
              <p className={`text-3xl font-bold mb-3`}>
                <span className={`${discount ? "line-through" : ""}`}>
                  {isAnnual ? "€690" : "€69"}&nbsp;
                </span>
                <span
                  className={` text-xs font-light text-[#F2F2F2] ${
                    discount ? "line-through" : ""
                  }`}
                >
                  {isAnnual ? t("perYear") : t("perMonth")}
                </span>
                <div className="text-sm font-light">
                  {discount
                    ? isAnnual
                      ? getDiscountPercentage(discount.discount, 690)
                      : getDiscountPercentage(discount.discount, 69)
                    : ""}
                </div>
              </p>
              <hr className="border-white rounded-3xl" />
            </div>
            <div className="text-xs font-light mb-3 text-[#BCBEC1]">
              +29€&nbsp;{t("perMonth")} {t("perProjectManager")}
            </div>
            <ul className="mb-6 text-sm">
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("UnlimitedProjects")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("UnlimitedProjectMembers")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("Resources workload")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("RiskManagement")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("ProjectDashboard")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("PortfolioDashboard")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("App Client")}
              </li>
              <li className="flex items-center mb-2">
                <span className="text-green-500 mr-2">
                  <img src={CharmTick} className="w-4 min-h-4" />
                </span>{" "}
                {t("OnboardingAndTraining")}
              </li>
            </ul>
            <Button
              variant={"primary"}
              disabled={
                (isPending && isRefetching) ||
                cancel(PlanTypeEnumSchema.ENTERPRISE)
                //   ||
                // (user?.subscription &&
                //   user?.subscription?.billingCycle !==
                //     (!isAnnual
                //       ? BillingCycleSchema.MONTH
                //       : BillingCycleSchema.YEAR))
              }
              isLoading={isPending || isRefetching}
              className="w-full   font-semibold"
              onClick={() => {
                allow(PlanTypeEnumSchema.ENTERPRISE)
                  ? invoicePortal()
                  : handleCheckout(PlanTypeEnumSchema.ENTERPRISE);
              }}
            >
              {allow(PlanTypeEnumSchema.ENTERPRISE)
                ? t("ViewInvoice")
                : text(PlanTypeEnumSchema.ENTERPRISE)}
            </Button>
            {cancel(PlanTypeEnumSchema.ENTERPRISE) &&
              user?.subscription?.endDate && (
                <span className="text-[0.65rem] text-center text-white/50">
                  {t(
                    "Your plan will be downgraded, but is still available until the end of your billing period on"
                  ) + ` (${dateFormater(user?.subscription?.endDate)})`}
                </span>
              )}
            {allow(PlanTypeEnumSchema.ENTERPRISE) && (
              <div className="absolute -bottom-6 right-1/2 translate-x-1/2 bg-primary-600 text-white px-1  rounded-b-xl  text-center">
                <span className="text-xs font-light text-white">
                  {t("CurrentPlan")}
                </span>
              </div>
            )}
          </div>
        </div>
        <Dialog
          isOpen={Boolean(errorMessage)}
          onClose={() => {}}
          modalClass="rounded-lg w-3/11"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={Warning} className="w-12 m-auto opacity-70" />{" "}
            {t(
              "You card will be charged only {{price}}€ for this {{duration}}. Please confirm this upgrade.",
              {
                price: errorMessage
                  ? errorMessage.split("{{")[1].split("}}")[0]
                  : "",
                duration: t(isAnnual ? "year" : "month"),
              }
            )}
            <div className="flex gap-2 ml-auto mt-4">
              <Button variant={"outline"} onClick={() => setErrorMessage("")}>
                {t("cancel")}
              </Button>
              <Button
                variant={"primary"}
                className={"text-white"}
                onClick={() => {
                  handleCheckout(PlanTypeEnumSchema.ENTERPRISE, true);
                }}
              >
                {t("confirm")}
              </Button>
            </div>
          </div>
        </Dialog>
        <Dialog
          isOpen={Boolean(downgrade)}
          onClose={() => {}}
          modalClass="rounded-lg w-3/11"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={Warning} className="w-12 m-auto opacity-70" />{" "}
            {t(
              "You'll be downgraded in the next billing cycle. Are you sure you want to downgrade?"
            )}
            <div className="flex gap-2 ml-auto mt-4">
              <Button
                variant={"outline"}
                onClick={() => setDowngrade(undefined)}
              >
                {t("cancel")}
              </Button>
              <Button
                variant={"primary"}
                className={"text-white"}
                onClick={() => {
                  downgrade ? handleCheckout(downgrade) : "";
                }}
              >
                {t("confirm")}
              </Button>
            </div>
          </div>
        </Dialog>
        <Dialog
          isOpen={isPaymentSuccess.loading}
          onClose={() => {}}
          modalClass="rounded-lg "
        >
          <div className=" w-80 h-fit flex flex-col gap-6 p-8 relative">
            {isPaymentSuccess.status == "ERROR" && (
              <div
                className="flex justify-end cursor-pointer items-center w-full h-1/4"
                onClick={() => {
                  setIsPaymentSuccess({ loading: false, status: "PENDING" });
                }}
              >
                <img src={crossNew} className="w-4 max-w-4" />
              </div>
            )}
            <div className="flex justify-center  w-full h-3/4   items-center">
              {isPaymentSuccess.status === "SUCCESS" ? (
                <div className="flex justify-center items-center animate-fadeInScale rounded-full w-fit h-fit p-4 bg-green-500/50">
                  <img src={GreenChackMark} className="w-20 h-20" />
                </div>
              ) : isPaymentSuccess.status == "PENDING" ? (
                <Spinner color="#299d91" className="h-20 w-20" />
              ) : (
                <div className="flex justify-center items-center animate-fadeInScale rounded-full w-fit h-fit p-4 bg-red-500/50">
                  <img src={RedCharmTick} className="w-20 h-20" />
                </div>
              )}
            </div>
            <div className="text-center text-xl font-semibold">
              {isPaymentSuccess.status === "SUCCESS"
                ? "Payment Success"
                : isPaymentSuccess.status == "PENDING"
                ? "Payment is in progress"
                : "Payment Failed"}
              <div className="text-center text-sm text-gray-450">
                {isPaymentSuccess.status === "SUCCESS" ? (
                  <div className="flex flex-col gap-2">
                    <p>{t("Your subscription is active now")}</p>
                    <Button
                      onClick={() => {
                        setIsPaymentSuccess({
                          loading: false,
                          status: "PENDING",
                        });
                        projectQuery.refetch();
                      }}
                      variant={"primary"}
                    >
                      Ok
                    </Button>
                  </div>
                ) : isPaymentSuccess.status == "PENDING" ? (
                  "Please wait while we process your payment"
                ) : (
                  <div className="flex flex-col gap-2">
                    <p> Please try again</p>
                    <Button
                      onClick={() => {
                        setIsPaymentSuccess({
                          loading: false,
                          status: "PENDING",
                        });
                        handleCheckout(PlanTypeEnumSchema.ENTERPRISE, true);
                      }}
                      variant={"primary"}
                    >
                      Try again
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentPlans;
